import { useEffect, useMemo, useRef, useState } from 'react';
import type { Account, Address, ComposeAttachment, Contact, Draft } from '@momomail/shared';
import { matchQuality, suggestContacts } from '@momomail/shared';
import { api, errorText } from '../lib/bridge';
import { COMPOSE_FILL, setAssistantContext } from '../lib/assistantContext';
import { displayName, fileSize } from '../lib/format';
import { Icon } from './Icon';
import { confirmDialog, Dialog } from './Overlay';
import { toast } from './Toasts';
import { t, tMessage } from '../lib/i18n';
import type { ParamsSchema } from '../lib/affordkit';
import { kit } from '../lib/screen';

const VALID = /^[^\s@<>()",;]+@[^\s@<>()",;]+\.[^\s@<>()",;]+$/;

function parseTokens(input: string): Address[] {
  const out: Address[] = [];
  for (const part of input.split(/[,;，；\n]+/)) {
    const s = part.trim();
    if (!s) continue;
    const m = s.match(/^(.*?)<([^<>]+)>$/);
    if (m) out.push({ name: m[1]!.trim().replace(/^"|"$/g, ''), address: m[2]!.trim() });
    else out.push({ name: '', address: s });
  }
  return out;
}

const TEXT: ParamsSchema = { type: 'object', properties: { text: { type: 'string', description: 'Addresses: "Name <address>" or address, comma separated' } }, required: ['text'] };

/**
 * Recipient input: Enter / comma turns the typed text into a small sticker; Backspace removes the last one; offers contact suggestions.
 * afford: its key for 馍馍, which writes the recipients as a whole (typed in, they would only become stickers on Enter or leaving it).
 */
function RecipientInput({ id, label, value, onChange, contacts, autoFocus, afford }: { id: string; label: string; value: Address[]; onChange: (v: Address[]) => void; contacts: Contact[]; autoFocus?: boolean; afford?: string }) {
  const [text, setText] = useState('');
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement | null>(null);
  const latest = useRef({ value, onChange, text });
  latest.current = { value, onChange, text };
  const register = (el: HTMLInputElement | null) => {
    input.current = el;
    if (!el || !afford) return;
    const set = (list: Address[]) => {
      setText('');
      latest.current.onChange(list);
    };
    const add = (raw: unknown) => parseTokens(String(raw ?? '')).filter((a) => !latest.current.value.some((v) => v.address.toLowerCase() === a.address.toLowerCase()));
    return kit.register(el, {
      key: afford,
      role: 'textbox',
      description: 'Recipients: "Name <address>", comma separated',
      ops: [
        { name: 'read', description: 'Who it goes to', run: () => [...latest.current.value.map((a) => (a.name ? `${a.name} <${a.address}>` : a.address)), latest.current.text.trim()].filter(Boolean).join(', ') },
        { name: 'write', description: 'Replace the recipients', params: TEXT, run: (args) => set(parseTokens(String(args.text ?? ''))) },
        { name: 'append', description: 'Add recipients', params: TEXT, run: (args) => set([...latest.current.value, ...add(args.text)]) },
        { name: 'clear', description: 'No recipients', run: () => set([]) },
      ],
    });
  };
  // Type part of a name or address; after a 150ms pause ask the backend for the best matches (not on every keystroke);
  // while waiting, keep the previous results minus ones that no longer match; if the backend is unreachable, use addresses from the mail at hand (offline)
  const [found, setFound] = useState<Contact[]>([]);
  const seq = useRef(0);
  const offline = useRef(contacts);
  offline.current = contacts;
  const query = text.trim();
  const filled = value.map((a) => a.address);
  const filledKey = filled.join(',');
  useEffect(() => {
    const n = ++seq.current;
    if (!query) return setFound([]);
    const exclude = filledKey ? filledKey.split(',') : [];
    const timer = setTimeout(() => {
      api()
        .mail.contacts(query, exclude)
        .catch(() => suggestContacts(offline.current, query, exclude))
        .then((list) => n === seq.current && setFound(list));
    }, 150);
    return () => clearTimeout(timer);
  }, [query, filledKey]);
  const suggestions = useMemo(
    () => (query ? found.filter((c) => matchQuality(c, query) > 0 && !filled.some((a) => a.toLowerCase() === c.address.toLowerCase())) : []).map((c): Address => ({ name: c.name, address: c.address })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [found, query, filledKey],
  );
  const commit = (raw = text) => {
    const tokens = parseTokens(raw);
    if (tokens.length) onChange([...value, ...tokens.filter((tok) => !value.some((v) => v.address.toLowerCase() === tok.address.toLowerCase()))]);
    setText('');
  };
  return (
    <div className="field-row" onClick={() => input.current?.focus()}>
      <label htmlFor={id} className="field-row__label">
        {label}
      </label>
      <div className="chips">
        {value.map((a, i) => (
          <span key={`${a.address}-${i}`} className={`chip ${VALID.test(a.address) ? '' : 'chip--bad'}`} title={a.address}>
            {a.name || a.address}
            <button
              className="bare chip__x"
              aria-label={t('移除 {address}', { address: a.address })}
              onClick={(e) => {
                e.stopPropagation();
                onChange(value.filter((_, j) => j !== i));
              }}
            >
              <Icon name="x" size={12} />
            </button>
          </span>
        ))}
        <input
          ref={register}
          id={id}
          className="chips__input"
          value={text}
          autoFocus={autoFocus}
          autoComplete="off"
          onChange={(e) => {
            const v = e.target.value;
            if (/[,;，；]/.test(v)) commit(v);
            else {
              setText(v);
              setActive(0);
            }
          }}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === 'Tab') && suggestions.length && text) {
              e.preventDefault();
              const s = suggestions[Math.min(active, suggestions.length - 1)]!;
              onChange([...value, s]);
              setText('');
            } else if (e.key === 'Enter' && text.trim()) {
              e.preventDefault();
              commit();
            } else if (e.key === 'Backspace' && !text && value.length) {
              onChange(value.slice(0, -1));
            } else if (e.key === 'ArrowDown' && suggestions.length) {
              e.preventDefault();
              setActive((a) => (a + 1) % suggestions.length);
            } else if (e.key === 'ArrowUp' && suggestions.length) {
              e.preventDefault();
              setActive((a) => (a - 1 + suggestions.length) % suggestions.length);
            }
          }}
          onBlur={() => setTimeout(() => text.trim() && commit(), 120)}
          onPaste={(e) => {
            const pasted = e.clipboardData.getData('text');
            if (/[,;，；\n]/.test(pasted)) {
              e.preventDefault();
              commit(text + pasted);
            }
          }}
        />
        {suggestions.length > 0 && (
          <div className="suggest" role="listbox">
            {suggestions.map((s, i) => (
              <button
                key={s.address}
                role="option"
                aria-selected={i === Math.min(active, suggestions.length - 1)}
                className={`suggest__item ${i === Math.min(active, suggestions.length - 1) ? 'is-active' : ''}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onChange([...value, s]);
                  setText('');
                }}
              >
                <strong>{displayName(s)}</strong>
                <span>{s.address}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const TITLES: Record<Draft['mode'], string> = { new: '写邮件', reply: '回复', replyAll: '全部回复', forward: '转发', draft: '继续写草稿' }; // i18n-ok

export function Compose({ initial, accounts, contacts, onDone }: { initial: Draft; accounts: Account[]; contacts: Contact[]; onDone: (sent: boolean) => void }) {
  const [draft, setDraft] = useState<Draft>(initial);
  const [open, setOpen] = useState(true);
  const [showCc, setShowCc] = useState(initial.cc.length > 0 || initial.bcc.length > 0);
  const [showQuote, setShowQuote] = useState(false);
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dirty = useRef(false);
  const latest = useRef(draft);
  latest.current = draft;

  const update = (patch: Partial<Draft>) => {
    dirty.current = true;
    setError(null);
    setDraft((d) => ({ ...d, ...patch }));
  };

  // For 馍馍: the email being written (it goes along with chat messages), and what it writes into it
  useEffect(() => {
    const who = (list: { name: string; address: string }[]) => list.map((a) => (a.name ? `${a.name} <${a.address}>` : a.address));
    setAssistantContext({ compose: { mode: draft.mode, replyTo: draft.sourceMessageId, to: who(draft.to), cc: who(draft.cc), subject: draft.subject, body: draft.body } });
  }, [draft]);
  useEffect(() => {
    const fill = (e: Event) => {
      const f = (e as CustomEvent<{ to?: string; cc?: string; subject?: string; body?: string }>).detail;
      if (f.cc) setShowCc(true);
      update({ ...(f.to !== undefined ? { to: parseTokens(f.to) } : {}), ...(f.cc !== undefined ? { cc: parseTokens(f.cc) } : {}), ...(f.subject !== undefined ? { subject: f.subject } : {}), ...(f.body !== undefined ? { body: f.body } : {}) });
    };
    window.addEventListener(COMPOSE_FILL, fill);
    return () => {
      window.removeEventListener(COMPOSE_FILL, fill);
      setAssistantContext({ compose: null });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save a local copy while typing (for crash recovery)
  useEffect(() => {
    if (!dirty.current) return;
    const timer = setTimeout(() => void api().compose.saveLocal(latest.current).catch(() => {}), 1200);
    return () => clearTimeout(timer);
  }, [draft]);

  const finish = (sent: boolean) => {
    setOpen(false);
    setTimeout(() => onDone(sent), 180);
  };

  const hasContent = () => {
    const d = latest.current;
    const sig = accounts.find((a) => a.id === d.accountId)?.signature ?? '';
    const body = d.body.replace(`-- \n${sig}`, '').trim();
    return dirty.current && (d.to.length > 0 || d.subject.trim() !== '' || body !== '' || d.attachments.length > 0);
  };

  const saveDraft = async (close: boolean) => {
    setSaving(true);
    try {
      const saved = await api().compose.saveDraft(latest.current);
      setDraft(saved);
      dirty.current = false;
      toast(t('草稿已保存'), 'ok');
      if (close) finish(false);
    } catch (e) {
      setError(t('草稿已存在本地，上传到服务器失败：{error}', { error: errorText(e) }));
    } finally {
      setSaving(false);
    }
  };

  const close = async () => {
    if (sending) return;
    if (!hasContent()) {
      await api().compose.discard({ ...latest.current, serverMessageId: null }).catch(() => {});
      return finish(false);
    }
    const choice = await confirmDialog({ title: t('保存这封草稿吗？'), message: t('不保存的话，写的内容会丢掉。'), confirm: t('保存草稿'), alt: t('不保存'), cancel: t('继续写') });
    if (choice === 'confirm') await saveDraft(true);
    else if (choice === 'alt') {
      await api().compose.discard({ ...latest.current, serverMessageId: latest.current.mode === 'draft' ? null : latest.current.serverMessageId }).catch(() => {});
      finish(false);
    }
  };

  const send = async () => {
    const d = latest.current;
    if (![...d.to, ...d.cc, ...d.bcc].length) return setError(t('请至少填一个收件人。'));
    const bad = [...d.to, ...d.cc, ...d.bcc].find((a) => !VALID.test(a.address));
    if (bad) return setError(t('这个地址看起来不对：{address}', { address: bad.address }));
    if (!d.subject.trim()) {
      const choice = await confirmDialog({ title: t('没有主题'), message: t('这封邮件没有写主题，仍然发送吗？'), confirm: t('仍然发送'), cancel: t('回去写主题') });
      if (choice !== 'confirm') return;
    }
    setSending(true);
    setError(null);
    try {
      const result = await api().compose.send(d);
      if (!result.ok) {
        setError(result.error ? tMessage(result.error) : t('发送失败'));
        return;
      }
      toast(result.warning ? tMessage(result.warning) : t('邮件已发出'), result.warning ? 'info' : 'ok');
      finish(true);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSending(false);
    }
  };

  const addFiles = (files: ComposeAttachment[]) => {
    if (!files.length) return;
    const total = [...latest.current.attachments, ...files].reduce((s, a) => s + a.size, 0);
    if (total > 25 * 1024 * 1024) toast(t('附件加起来超过 25 MB，很多邮箱会拒收。'), 'error');
    update({ attachments: [...latest.current.attachments, ...files] });
  };

  const account = accounts.find((a) => a.id === draft.accountId);
  const footer = (
      <div className="compose__foot">
        <button className="btn btn--primary" data-afford-key="compose.send" data-afford-policy="deny" onClick={() => void send()} disabled={sending}>
          {sending ? <span className="spinner" /> : <Icon name="send" size={16} />}
          {sending ? t('发送中…') : t('发送')}
        </button>
        <span className="compose__hint muted">
          <kbd>Ctrl</kbd> + <kbd>Enter</kbd>
        </span>
        <span className="compose__spacer" />
        <button className="btn btn--small btn--quiet" onClick={() => void api().compose.pickFiles().then(addFiles).catch((err) => toast(errorText(err), 'error'))} disabled={sending}>
          <Icon name="clip" size={15} />
          {t('附件')}
        </button>
        <button className="btn btn--small btn--quiet" onClick={() => void saveDraft(false)} disabled={sending || saving} title={t('存草稿（Ctrl+S）')}>
          {saving ? <span className="spinner" /> : <Icon name="draft" size={15} />}
          {t('存草稿')}
        </button>
        <button
          className="btn btn--small btn--quiet tool--danger"
          disabled={sending}
          title={t('丢弃')}
          onClick={async () => {
            if (hasContent()) {
              const c = await confirmDialog({ title: t('丢弃这封邮件？'), message: t('写的内容和已保存的草稿都会删掉。'), confirm: t('丢弃'), danger: true });
              if (c !== 'confirm') return;
            }
            await api().compose.discard(latest.current).catch(() => {});
            finish(false);
          }}
        >
          <Icon name="trash" size={15} />
        </button>
      </div>
  );

  return (
    <Dialog open={open} onClose={() => void close()} title={t(TITLES[draft.mode])} labelledBy="compose-title" className="compose" dismissable={!sending} footer={footer}>
      <div
        className={`compose__form ${dragging ? 'is-dragging' : ''}`}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            void send();
          }
          if (e.key === 's' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            void saveDraft(false);
          }
        }}
        onDragOver={(e) => {
          if (e.dataTransfer.types.includes('Files')) {
            e.preventDefault();
            setDragging(true);
          }
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragging(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          const files = Array.from(e.dataTransfer.files);
          if (files.length)
            void api()
              .compose.upload(files)
              .then(addFiles)
              .catch((err) => toast(errorText(err), 'error'));
        }}
      >
        <div className="field-row">
          <label htmlFor="compose-from" className="field-row__label">
            {t('发件人')}
          </label>
          <select id="compose-from" className="field-row__select" value={draft.accountId} onChange={(e) => update({ accountId: e.target.value })} disabled={draft.mode !== 'new'}>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.displayName || a.name} &lt;{a.email}&gt;
              </option>
            ))}
          </select>
          {account && <i className="field-row__acc" style={{ background: account.color }} />}
        </div>
        <RecipientInput id="compose-to" label={t('收件人')} value={draft.to} onChange={(to) => update({ to })} contacts={contacts} autoFocus={draft.to.length === 0} afford="compose.to" />
        {!showCc && (
          <button className="compose__cc-toggle" data-afford-key="compose.add_cc" onClick={() => setShowCc(true)}>
            {t('抄送 / 密送')}
          </button>
        )}
        {showCc && (
          <>
            <RecipientInput id="compose-cc" label={t('抄送')} value={draft.cc} onChange={(cc) => update({ cc })} contacts={contacts} afford="compose.cc" />
            <RecipientInput id="compose-bcc" label={t('密送')} value={draft.bcc} onChange={(bcc) => update({ bcc })} contacts={contacts} afford="compose.bcc" />
          </>
        )}
        <div className="field-row">
          <label htmlFor="compose-subject" className="field-row__label">
            {t('主题')}
          </label>
          <input id="compose-subject" data-afford-key="compose.subject" className="field-row__input" value={draft.subject} onChange={(e) => update({ subject: e.target.value })} placeholder={t('写个主题')} autoFocus={draft.to.length > 0 && !draft.subject} />
        </div>
        <textarea
          className="compose__body"
          aria-label={t('正文')}
          data-afford-key="compose.body"
          data-afford-describe="The text of the email (for a reply, the quote of the original is kept apart, below it)"
          value={draft.body}
          onChange={(e) => update({ body: e.target.value })}
          placeholder={t('想说点什么…')}
          autoFocus={draft.to.length > 0 && !!draft.subject}
          onFocus={(e) => {
            // Put the cursor at the start (signature and quote come after)
            if (draft.mode !== 'draft' && !dirty.current) e.currentTarget.setSelectionRange(0, 0);
          }}
        />
        {draft.quoted && (
          <div className="compose__quote">
            <button className="compose__quote-toggle" onClick={() => setShowQuote((v) => !v)} aria-expanded={showQuote}>
              <Icon name="more" size={16} />
              {showQuote ? t('收起原文') : draft.mode === 'forward' ? t('显示转发的原文') : t('显示引用的原文')}
            </button>
            {showQuote && <pre className="compose__quote-text">{draft.quoted}</pre>}
          </div>
        )}
        {draft.attachments.length > 0 && (
          <div className="compose__files">
            {draft.attachments.map((a) => (
              <span key={a.id} className="file-chip">
                <Icon name="clip" size={14} />
                <span className="file-chip__name" title={a.filename}>
                  {a.filename}
                </span>
                <span className="file-chip__size">{fileSize(a.size)}</span>
                <button className="bare" aria-label={t('移除附件 {name}', { name: a.filename })} onClick={() => update({ attachments: draft.attachments.filter((x) => x.id !== a.id) })}>
                  <Icon name="x" size={13} />
                </button>
              </span>
            ))}
          </div>
        )}
        {dragging && (
          <div className="compose__drop" aria-hidden>
            <Icon name="clip" size={28} />
            {t('松手添加附件')}
          </div>
        )}
        {error && (
          <div className="notice notice--error compose__error" role="alert">
            {error}
          </div>
        )}
      </div>
    </Dialog>
  );
}
